/**
 * ADO #287 Phase 1 — Output template parser + format-spec grammar.
 *
 * Single source of truth for parsing the Output node's template string.
 * Both the validator (nodeValidationHelpers) and the C codegen
 * (buildOutputStatement) consume `parseOutputTemplate` so they cannot
 * disagree on what the template means.
 *
 * Template grammar (Phase 1):
 *
 *   template     := segment*
 *   segment      := literal | escaped-brace | placeholder
 *   literal      := any chars except '{' or '}'
 *   escaped-brace := '{{'  →  literal '{'
 *                  | '}}'  →  literal '}'
 *   placeholder  := '{' name (':' spec)? '}'
 *   name         := IDENT ( '.' IDENT | '[' INDEX ']' )*
 *                  | a function-call expression (e.g. f(x))
 *                  | an arithmetic expression (e.g. a+b) — a `:spec` IS allowed (ADO #456)
 *   IDENT        := [A-Za-z_][A-Za-z0-9_]*
 *   INDEX        := [0-9]+ | IDENT
 *   spec         := align? sign? '0'? width? ('.' precision)? type?
 *   align        := '<' | '>' | '=' | '^'
 *   sign         := '+' | '-' | ' '
 *   width        := [0-9]+   (capped at 100)
 *   precision    := [0-9]+   (capped at 100)
 *   type         := 'b' | 'c' | 'd' | 'e' | 'E' | 'f' | 'F'
 *                 | 'g' | 'G' | 'o' | 'x' | 'X' | 's' | '%'
 *
 * ADO #456 lifted the Phase-1 refusal of arithmetic-with-spec: `{a+b:.2f}` is
 * now accepted (codegen already emits it). Bare `{a+b}` still passes through to
 * the existing inference path.
 */

// Bounded grammar — see jsdoc above. Single source of truth used by
// both the parser and the validator. Anchored so partial matches fail.
const SPEC_RE = /^([<>=^])?([+\- ])?(0)?([0-9]{0,3})?(?:\.([0-9]{1,3}))?([bcdeEfFgGoxXs%])?$/;

// Placeholder grammar: the bit between `{` and `}`. Capture the name part
// (everything up to the optional ':') and the spec part (after ':').
//
// Names support: simple ident, dotted member access (point.x), indexed
// access (arr[0], arr[i]), function calls (f(x, y)), and arithmetic
// expressions (price * qty). ADO #456 — a `:spec` is allowed on ANY of these
// (codegen supports `{price * qty:.2f}`); only the spec *grammar* is checked
// here, and the name→spec *type* check stays gated to simple names elsewhere.

/**
 * Parse a template into segments. Returns an array of:
 *   { type: 'literal', value: string }
 *   { type: 'placeholder', raw: string, name: string, spec: string|null,
 *       error: string|null }   // error set if grammar invalid
 *
 * Throws on truly unparseable input (unbalanced braces); callers
 * (validator, codegen) should rely on validation to catch grammar
 * issues before attempting codegen.
 */
export function parseOutputTemplate(template) {
  const segments = [];
  let i = 0;
  let buf = '';
  const flushLiteral = () => {
    if (buf.length > 0) {
      segments.push({ type: 'literal', value: buf });
      buf = '';
    }
  };

  while (i < template.length) {
    const ch = template[i];

    // {{  →  literal {
    if (ch === '{' && template[i + 1] === '{') {
      buf += '{';
      i += 2;
      continue;
    }
    // }}  →  literal }
    if (ch === '}' && template[i + 1] === '}') {
      buf += '}';
      i += 2;
      continue;
    }
    // Stray '}' without escape — error
    if (ch === '}') {
      flushLiteral();
      segments.push({
        type: 'placeholder',
        raw: '}',
        name: '',
        spec: null,
        error: "Unexpected '}' (use '}}' for a literal closing brace)",
      });
      i += 1;
      continue;
    }
    // '{' starts a placeholder. Find matching '}'. Inside, no nested
    // braces (Phase 1) — `{a + {b}}` is rejected.
    if (ch === '{') {
      flushLiteral();
      const start = i + 1;
      let end = -1;
      for (let j = start; j < template.length; j++) {
        const c = template[j];
        if (c === '{') {
          // Nested unescaped '{' — error
          end = -2;
          break;
        }
        if (c === '}') {
          end = j;
          break;
        }
      }
      if (end === -1) {
        // Unterminated
        segments.push({
          type: 'placeholder',
          raw: template.substring(i),
          name: '',
          spec: null,
          error: 'Missing closing brace }',
        });
        i = template.length;
        continue;
      }
      if (end === -2) {
        segments.push({
          type: 'placeholder',
          raw: '',
          name: '',
          spec: null,
          error: "Nested '{' inside placeholder is not supported",
        });
        // Skip to after the inner brace would still leave a mess; bail out
        // of placeholder mode so the outer loop continues sanely.
        i += 1;
        continue;
      }

      const inner = template.substring(start, end);
      const colonIdx = _splitNameAndSpec(inner);
      let name = inner;
      let spec = null;
      if (colonIdx !== -1) {
        name = inner.substring(0, colonIdx);
        spec = inner.substring(colonIdx + 1);
      }
      const trimmedName = name.trim();
      const trimmedSpec = spec === null ? null : spec.trim();

      let error = null;
      if (trimmedName === '') {
        error = 'Empty placeholder {} found';
      } else if (_bracketError(trimmedName)) {
        error = _bracketError(trimmedName);
      } else if (trimmedSpec !== null) {
        // ADO #456 — a format spec is allowed on an arithmetic/expression
        // placeholder too (`{price * qty:.2f}`), not just plain names — codegen
        // supports it. Validate the spec grammar; the name→spec *type* check
        // (in nodeValidationHelpers) stays gated to simple names it can resolve.
        const specErr = validateFormatSpec(trimmedSpec);
        if (specErr) error = specErr;
      }

      segments.push({
        type: 'placeholder',
        raw: template.substring(i, end + 1),
        name: trimmedName,
        spec: trimmedSpec,
        error,
      });
      i = end + 1;
      continue;
    }

    buf += ch;
    i += 1;
  }
  flushLiteral();
  return segments;
}

/**
 * Unbalanced `[` / `]` in a placeholder name.
 *
 * The name grammar admits several shapes — plain ident, dotted member access,
 * indexed access, a call, an arithmetic expression — so the name cannot be
 * matched against one pattern. Bracket balance, though, is wrong in every one
 * of them, and getting it wrong is silent all the way to the compiler:
 *
 *   {scores[i}  →  name "scores[i"  →  printf("%d ", scores[i);
 *
 * which fails as `expected ']' before ')' token`, in generated C the learner
 * never wrote and is not supposed to read. The validator has always checked
 * PARENTHESES this way (nodeValidationHelpers) and simply never checked
 * brackets; this puts the check in the parser instead, where both the
 * validator and codegen already read `seg.error` from.
 *
 * Returns a message, or null when balanced.
 */
function _bracketError(name) {
  let depth = 0;
  for (const c of name) {
    if (c === '[') depth += 1;
    else if (c === ']') {
      depth -= 1;
      if (depth < 0) return `Syntax error in {${name}}: unexpected ]`;
    }
  }
  return depth > 0 ? `Syntax error in {${name}}: missing ]` : null;
}

// Find the colon that separates name from spec. Skips ':' that sits
// inside `[...]` (so `arr[i:j]` — if we ever support slicing — would
// be safe). Returns -1 when no separator.
function _splitNameAndSpec(inner) {
  let depth = 0;
  for (let i = 0; i < inner.length; i++) {
    const c = inner[i];
    if (c === '[' || c === '(') depth += 1;
    else if (c === ']' || c === ')') depth -= 1;
    else if (c === ':' && depth === 0) return i;
  }
  return -1;
}

/**
 * Validate a format spec against the grammar. Returns null on success,
 * an error string otherwise. Caps width / precision at 100 to prevent
 * `printf("%1000d", x)` from being a buffer-fattening footgun.
 */
export function validateFormatSpec(spec) {
  if (spec === null || spec === undefined) return null;
  if (spec === '') return "Empty format spec after ':'";
  const m = SPEC_RE.exec(spec);
  if (!m) return `Invalid format spec ':${spec}'`;
  const widthStr = m[4];
  const precStr = m[5];
  if (widthStr) {
    const w = parseInt(widthStr, 10);
    if (w > 100) return `Format spec width ${w} exceeds the maximum of 100`;
  }
  if (precStr) {
    const p = parseInt(precStr, 10);
    if (p > 100) return `Format spec precision ${p} exceeds the maximum of 100`;
  }
  return null;
}

/**
 * Decompose a validated spec into its parts. Returns null when spec is
 * null/empty, otherwise `{align, sign, zero, width, precision, type}`.
 */
export function parseFormatSpec(spec) {
  if (!spec) return null;
  const m = SPEC_RE.exec(spec);
  if (!m) return null;
  return {
    align: m[1] || null,
    sign: m[2] || null,
    zero: !!m[3],
    width: m[4] ? parseInt(m[4], 10) : null,
    precision: m[5] ? parseInt(m[5], 10) : null,
    type: m[6] || null,
  };
}

// ---------------------------------------------------------------------------
// Type ↔ spec compatibility
// ---------------------------------------------------------------------------

const FLOAT_TYPES = new Set(['float', 'double']);
const INT_TYPES = new Set(['int', 'integer', 'long', 'long long', 'short', 'unsigned', 'unsigned int', 'long_long']);
const STRING_TYPES = new Set(['string', 'char*', 'char[]']);
const CHAR_TYPES = new Set(['char']);

// printf type letters that REQUIRE a numeric argument (float-friendly)
const FLOAT_SPEC_TYPES = new Set(['e', 'E', 'f', 'F', 'g', 'G']);
// printf type letters that REQUIRE an integral argument
const INT_SPEC_TYPES = new Set(['b', 'd', 'o', 'x', 'X']);
// String / char specs
const STRING_SPEC_TYPES = new Set(['s']);
const CHAR_SPEC_TYPES = new Set(['c']);

function _normalizeType(t) {
  return (t || '').toLowerCase().trim();
}

/**
 * Validate that a format spec is compatible with the variable's data
 * type. Returns null on success, a human-readable error string on
 * mismatch. Bare bareSpec === null is always compatible (no explicit
 * spec — codegen's existing inference handles it).
 *
 * The matrix is intentionally loose around ints (any integral type can
 * use `:d`, `:x`, `:o`) but strict between numeric / string / char so
 * the C runtime never gets a type mismatch (which is undefined
 * behaviour on most platforms).
 */
export function validateTypeAgainstSpec(spec, dataType) {
  if (!spec) return null;
  const parsed = parseFormatSpec(spec);
  if (!parsed) return null; // grammar already rejected upstream
  const t = parsed.type;
  if (!t) return null; // no explicit type letter — width/precision-only spec is type-agnostic
  if (t === '%') return null; // literal percent

  const dt = _normalizeType(dataType);

  if (FLOAT_SPEC_TYPES.has(t)) {
    if (!FLOAT_TYPES.has(dt) && !INT_TYPES.has(dt)) {
      return `Format ':${spec}' expects a number; '${dataType || 'unknown'}' is not numeric`;
    }
    return null;
  }
  if (INT_SPEC_TYPES.has(t)) {
    if (!INT_TYPES.has(dt) && !CHAR_TYPES.has(dt)) {
      return `Format ':${spec}' expects an integer; '${dataType || 'unknown'}' is not integral`;
    }
    return null;
  }
  if (STRING_SPEC_TYPES.has(t)) {
    if (!STRING_TYPES.has(dt)) {
      return `Format ':${spec}' expects a string; '${dataType || 'unknown'}' is not a string`;
    }
    return null;
  }
  if (CHAR_SPEC_TYPES.has(t)) {
    if (!CHAR_TYPES.has(dt) && !INT_TYPES.has(dt)) {
      return `Format ':${spec}' expects a char (or int code point); '${dataType || 'unknown'}' is incompatible`;
    }
    return null;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Per-language emitter helpers (Phase 1 ships C only).
// ---------------------------------------------------------------------------

/**
 * Build a C printf-style format specifier from a parsed spec + the
 * variable's data type. Auto-promotes long / long long to %ld / %lld
 * when the spec type is `d` / `x` / `o` and the data type warrants it.
 *
 * Examples:
 *   spec=':.2f', dt='float'      → '%.2f'
 *   spec=':5d',  dt='long long'  → '%5lld'
 *   spec=':x',   dt='long'       → '%lx'
 *   spec=':d',   dt='int'        → '%d'
 */
export function buildCFormatFromSpec(spec, dataType) {
  if (!spec) return null;
  const parsed = parseFormatSpec(spec);
  if (!parsed) return null;

  // Translate Python-style align '<' / '>' to printf's '-' (left-align)
  // since printf has no right-align flag (right-align is default).
  // '=' and '^' have no direct printf equivalent — we drop them with a
  // best-effort fallback to default alignment. The validator catches
  // truly malformed specs upstream so this lossy mapping is OK.
  let flags = '';
  if (parsed.align === '<') flags += '-';
  if (parsed.sign === '+') flags += '+';
  if (parsed.sign === ' ') flags += ' ';
  if (parsed.zero) flags += '0';

  const width = parsed.width !== null ? String(parsed.width) : '';
  const precision = parsed.precision !== null ? `.${parsed.precision}` : '';

  // Length modifier (auto-promote integer specs based on dataType).
  const dt = _normalizeType(dataType);
  let lenMod = '';
  let type = parsed.type || '';
  if (INT_SPEC_TYPES.has(type) || (CHAR_SPEC_TYPES.has(type) && INT_TYPES.has(dt))) {
    if (dt === 'long long' || dt === 'long_long') lenMod = 'll';
    else if (dt === 'long') lenMod = 'l';
    else if (dt === 'short') lenMod = 'h';
  } else if (FLOAT_SPEC_TYPES.has(type) && dt === 'double') {
    // %f / %e / %g already accept double in modern C (default arg
    // promotion). No length modifier needed; %lf is also accepted but
    // canonical is bare %f. Leave as-is.
    lenMod = '';
  }

  // Default the type letter when omitted but width/precision present —
  // pick from the data type to keep behaviour predictable.
  if (!type) {
    if (FLOAT_TYPES.has(dt)) type = 'f';
    else if (STRING_TYPES.has(dt)) type = 's';
    else if (CHAR_TYPES.has(dt)) type = 'c';
    else type = 'd';
  }

  return `%${flags}${width}${precision}${lenMod}${type}`;
}

/**
 * Convenience: does the parsed segments list contain ANY explicit spec?
 * Used by codegen to decide whether to inject `setlocale(LC_ALL, "C")`
 * at program start (so float formatting is locale-stable).
 */
export function hasAnyExplicitSpec(segments) {
  return segments.some((s) => s.type === 'placeholder' && s.spec);
}
